import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "According to the PSQI framework, what is the ideal sleep efficiency percentage?",
    options: [
      "75% or higher",
      "85% or higher",
      "95% or higher",
      "Sleep efficiency isn't measured in PSQI"
    ],
    correctAnswer: 1,
    explanation: "The PSQI measures sleep efficiency as the percentage of time asleep while in bed. An efficiency of 85% or higher is considered good quality sleep."
  },
  {
    question: "Which of the following is NOT a component measured by the PSQI (Pittsburgh Sleep Quality Index)?",
    options: [
      "Sleep duration",
      "Sleep latency (time to fall asleep)",
      "Dream content and vividness",
      "Daytime sleepiness"
    ],
    correctAnswer: 2,
    explanation: "The PSQI measures 7 components: sleep quality, latency, duration, efficiency, disturbances, medication use, and daytime dysfunction. Dream content is not one of the measured components."
  },
  {
    question: "What is the recommended cutoff time for caffeine consumption to avoid sleep interference?",
    options: [
      "Noon",
      "3 PM",
      "6 PM",
      "9 PM"
    ],
    correctAnswer: 1,
    explanation: "Caffeine has a half-life of 5-6 hours, so consuming it after 3 PM can interfere with sleep onset and quality. It's recommended to avoid caffeine in the afternoon and evening."
  },
  {
    question: "Which sleep habit is most likely to disrupt your circadian rhythm?",
    options: [
      "Reading a book before bed",
      "Going to bed and waking up at different times each day",
      "Keeping the bedroom cool",
      "Having a light snack before bed"
    ],
    correctAnswer: 1,
    explanation: "Inconsistent sleep and wake times disrupt your body's internal clock (circadian rhythm), making it harder to fall asleep and wake up naturally. Consistency is key for good sleep hygiene."
  },
  {
    question: "What does the term 'sleep latency' refer to?",
    options: [
      "How long you sleep each night",
      "How quickly you fall asleep",
      "How many times you wake up",
      "How tired you feel during the day"
    ],
    correctAnswer: 1,
    explanation: "Sleep latency specifically refers to the amount of time it takes to fall asleep after getting into bed. Ideally, this should be less than 30 minutes for good sleep quality."
  }
];

const PhysicalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('physicalM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of sleep wellness</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Sleep hygiene and healthy habits</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Energy and recovery strategies</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your sleep wellness journey by practicing good sleep hygiene!</p>
            <p>Ready to deepen your understanding of rest and recovery?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/physical-resources')}
          >
            Return to Physical Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left physical-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Understanding Sleep Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/physical-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule2QuizPage;

